import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import GeneralInput from "../../common/GeneralInput";
import Modal from "../../common/Modal";
import { useAdmin } from "../../../context/admin";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { TourInfo, Zone, Subzone, TourEntrada } from "../../../types/common.types";
import AdminService from "../../../services/admin.service";
import includes from "../../../json/tour_includes.json"
import Select from "../../common/Select";
import Component from "../../common/Component";
import insert from "../../../utils/insert";
import jsonToFormData from "../../../utils/jsonToFormData";
import { useGlobal } from "../../../context/global";
import FileInput from "../../common/FileInput";
import Checkbox from "../../common/Checkbox";
import styled from "styled-components";

// Function to convert image to WebP
async function convertToWebP(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    // Check if file is already WebP
    if (file.type === 'image/webp') {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0);
        
        // Convert to WebP with quality settings
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not convert image to WebP'));
              return;
            }
            
            // Create new File object with WebP extension
            const webpFile = new File(
              [blob],
              `${file.name.split('.')[0]}.webp`,
              { type: 'image/webp' }
            );
            resolve(webpFile);
          },
          'image/webp',
          0.85 // 85% quality - adjust as needed
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
}

const FRANJAS = ['AM', 'PM', 'FULL_DAY', 'NIGHT']
const CATEGORIAS = ['general', 'joven', 'niño', 'infante', 'adulto mayor', 'estudiante', 'necesidades especiales', 'indígena', 'atacameño']

export default function EditTourInfo() {
    const { setIsLoading, setMessage } = useGlobal()
    const {editTourInfoId, user, setEditTourInfoId} = useAdmin()
    const [zones, setZones] = useState<Zone[]>([])
    const [subzones, setSubzones] = useState<Subzone[]>([])

    const [data, setData] = useState<Partial<TourInfo>>({})

    const includes_options = includes.filter(i => data.include ? !data.include.includes(i) : true).map((i) => ({name: i, value: i}))
    const franjas_options = FRANJAS.filter(f => data.franjas ? !data.franjas.includes(f) : true).map((f) => ({name: f, value: f}))

    useEffect(() => {
        async function getInfo() {
            if (!editTourInfoId || !user) return
            setIsLoading(true)
            try {
                const entry = {
                    tour_id: editTourInfoId,
                    agency_id: user.user.agency_id
                }
                const response = await AdminService.getTourInfo(entry)
                const {created_at, ...rest} = response.data
                setData({active: true, ...rest, ...entry})
                const response_zones = await AdminService.listZones()
                setZones(response_zones.data)
                const response_subzones = await AdminService.listSubzones()
                setSubzones(response_subzones.data)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        getInfo()
    }, [editTourInfoId, user])

    function onChange(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value
        const name = e.target.name
        setData(prevData  => ({...prevData, [name]: value }))
    }
    
    function onChangeIncludes(e: ChangeEvent<HTMLSelectElement>) {
        const value = e.target.value
        const newIncludes = data.include ? [...data.include, value] : [value]
        setData(prevData => ({...prevData, include: newIncludes}))
    }
    
    function deleteIncludes(incl: string) {
        const newIncludes = data.include?.filter(i => i !== incl)
        setData(prevData => ({...prevData, include: newIncludes}))
    }

    function onChangeFranjas(e: ChangeEvent<HTMLSelectElement>) {
        const value = e.target.value
        const newFranjas = data.franjas ? [...data.franjas, value] : [value]
        setData(prevData => ({...prevData, franjas: newFranjas}))
    }

    function deleteFranja(fr: string) {
        const newFranjas = data.franjas?.filter(f => f !== fr)
        setData(prevData => ({...prevData, franjas: newFranjas}))
    }

    function addEntrada() {
        const item: TourEntrada = {parque: '', categoria: '', nacionalidad: 'ambos', edad_min: '', edad_max: '', requiere_acreditacion: false, precio_clp: '', activa: true}
        const list = data.entradas ? [...data.entradas, item] : [item]
        setData(prevData => ({...prevData, entradas: list}))
    }

    function updateEntrada(idx: number, field: keyof TourEntrada, value: any) {
        if (!data.entradas?.length) return
        const item = {...data.entradas[idx], [field]: value}
        const filtered = data.entradas.filter((_, i) => i !== idx)
        const list = insert(filtered, idx, item)
        setData(prevData => ({...prevData, entradas: list}))
    }

    function deleteEntrada(idx: number) {
        const list = data.entradas?.filter((_, i) => i !== idx)
        setData(prevData => ({...prevData, entradas: list}))
    }

    function deleteDescription(idx: number) {
        if (!data.detailed_description?.length) return
        const newDesc = data.detailed_description.filter((dd, indx) => indx !== idx)
        setData(prevData => ({...prevData, detailed_description: newDesc}))
    }
    
    function addDescription() {
        const newItem = {name: '', name_es:  '', name_pt: '', description: '', description_es: '', description_pt: ''}
        const newDesc = data.detailed_description ?  [...data.detailed_description, newItem] : [newItem]
        setData(prevData => ({...prevData, detailed_description: newDesc}))
    }
    
    function onChangeDesc(e: ChangeEvent<HTMLInputElement>, idx: number) {
        if (!data.detailed_description?.length) return
        const name = e.target.name
        const value = e.target.value
        const newItem = {...data.detailed_description[idx], [name]: value}
        const filteredList = data.detailed_description.filter((dd, indx) => indx !== idx)
        const newDesc = insert(filteredList, idx, newItem)
        setData(prevData => ({...prevData, detailed_description: newDesc}))
    }

    function addFAQ() {
        const newItem = {answer: '', answer_es:  '', answer_pt: '', question: '', question_es: '', question_pt: ''}
        const newFAQ = data.faq ?  [...data.faq, newItem] : [newItem]
        setData(prevData => ({...prevData, faq: newFAQ}))
    }

    function deleteFAQ(idx: number) {
        if (!data.faq?.length) return
        const newFAQ = data.faq.filter((dd, indx) => indx !== idx)
        setData(prevData => ({...prevData, faq: newFAQ}))
    }

    function onChangeFAQ(e: ChangeEvent<HTMLInputElement>, idx: number) {
        if (!data.faq?.length) return
        const name = e.target.name
        const value = e.target.value
        const newItem = {...data.faq[idx], [name]: value}
        const filteredList = data.faq.filter((dd, indx) => indx !== idx)
        const newDesc = insert(filteredList, idx, newItem)
        setData(prevData => ({...prevData, faq: newDesc}))
    }
    
    // Function to handle file upload with WebP conversion
    const handleFileChange = async (newFile: File, fieldName: keyof TourInfo) => {
      try {
        setIsLoading(true);
        const webpFile = await convertToWebP(newFile);
        setData(prevData => ({...prevData, [fieldName]: webpFile}));
        setMessage({
          type: 'success',
          content: `Image converted to WebP (${Math.round(webpFile.size / 1024)}KB)`
        });
        setTimeout(() => {
          setMessage(undefined);
        }, 2000);
      } catch (error) {
        console.error('Error converting to WebP:', error);
        setMessage({
          type: 'error',
          content: 'Failed to convert image. Uploading original format.'
        });
        // Fallback: use original file
        setData(prevData => ({...prevData, [fieldName]: newFile}));
        setTimeout(() => {
          setMessage(undefined);
        }, 2000);
      } finally {
        setIsLoading(false);
      }
    };

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        if (!editTourInfoId) return
        try {
            const {id, zone_id ,...rest} = data
            console.log(rest);
            console.log(data);
            
            if (zone_id) await AdminService.assignZone(editTourInfoId, zone_id)
            
            // Convert any remaining image files to WebP before creating form data
            const processedData = { ...rest };
            const imageFields = ['photo1', 'photo2', 'photo3', 'photo4', 'photo5', 'photo6', 'cover'] as const;
            
            for (const field of imageFields) {
              if (processedData[field] instanceof File) {
                try {
                  processedData[field] = await convertToWebP(processedData[field] as File);
                } catch (error) {
                  console.error(`Failed to convert ${field} to WebP:`, error);
                  // Keep original file if conversion fails
                }
              }
            }
            
            const formData = jsonToFormData(processedData)
            const response = await AdminService.handleTourInfo(formData)
            await window.location.reload()
        } catch (error: any) {
            console.log(error);
            setMessage({
                type: 'error',
                content: error.response?.data || 'Error'
            })
            setTimeout(() => {
                setMessage(undefined)
            }, 2500)
        } finally {
            setIsLoading(false)
        }
    }

    const zones_list = useMemo(() => {
        return zones.map(z => ({name: z.id, value: z.id}))
    }, [zones])
    const subzones_list = useMemo(() => {
        return subzones.filter(sz => sz.zone_id === data.zone_id).map(sz => ({name: sz.nombre || sz.id, value: sz.id}))
    }, [subzones, data.zone_id])
    return (
        <Modal
        onSubmit={onSubmit}
        >
            <h1>Edit tour info</h1>
            <div className="close" title="close"
                onClick={() => setEditTourInfoId(undefined)}
                >
                    <AiOutlineCloseCircle/>
            </div>
            <Select
            style={{marginBottom: '20px'}}
            label="Zone"
            defaultNull
            onChange={e => setData(prevData => ({...prevData, zone_id: e.target.value, subzone_id: ''}))}
            value={data.zone_id}
            options={zones_list}
            />
            <Select
            style={{marginBottom: '20px'}}
            label={data.zone_id ? "Subzone (nivel de servicio)" : "Subzone (elige una zona primero)"}
            defaultNull
            onChange={e => setData(prevData => ({...prevData, subzone_id: e.target.value}))}
            value={data.subzone_id || ''}
            options={subzones_list}
            />
            <div className="input-container">
                <GeneralInput
                label="Title (en)"
                value={data.title || ''}
                name="title"
                onChange={onChange}
                style={{marginBottom: '10px'}}
                />
                <GeneralInput
                label="Title (es)"
                value={data.title_es || ''}
                name="title_es"
                onChange={onChange}
                style={{marginBottom: '10px'}}
                />
                <GeneralInput
                label="Title (pt)"
                value={data.title_pt || ''}
                name="title_pt"
                onChange={onChange}
                style={{marginBottom: '10px'}}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Current price (per person)"
                value={data.current_price || ''}
                onChange={onChange}
                type="number"
                placeholder="100.00"
                name="current_price"
                style={{marginBottom: '20px'}}
                />
                <GeneralInput
                label="Discount price (per person)"
                value={data.discount_price || ''}
                onChange={onChange}
                type="number"
                placeholder="100.00"
                name="discount_price"
                style={{marginBottom: '20px'}}
                />
                <GeneralInput
                label="Depart price (per person)"
                value={data.depart_price || ''}
                onChange={onChange}
                type="number"
                placeholder="100.00"
                name="depart_price"
                style={{marginBottom: '20px'}}
                />
            </div>
            <h2>Operación y atributos</h2>
            <div className="input-container">
                <Select
                label="Unidad de precio"
                onChange={e => setData(prevData => ({...prevData, unidad_precio: e.target.value}))}
                value={data.unidad_precio || 'por_persona'}
                options={[{name: 'Por persona', value: 'por_persona'}, {name: 'Por vehículo', value: 'por_vehiculo'}]}
                />
                <GeneralInput
                label="Capacidad ofrecida (cupo, ej. privado 4)"
                value={data.capacidad_ofrecida ?? ''}
                name="capacidad_ofrecida"
                type="number"
                onChange={onChange}
                />
                <GeneralInput
                label="Duración (horas)"
                value={data.duracion_horas ?? ''}
                name="duracion_horas"
                type="number"
                onChange={onChange}
                />
            </div>
            <h3 style={{marginBottom: '4px'}}>Franjas horarias</h3>
            <Select
            value={''}
            defaultNull
            options={franjas_options}
            onChange={onChangeFranjas}
            />
            <div className="list" style={{margin: '10px 0'}}>
                {
                    data.franjas?.map((f, idx) => (
                        <Component key={idx} close={() => deleteFranja(f)}>
                            {f}
                        </Component>
                    ))
                }
            </div>
            <div className="input-container">
                <GeneralInput
                label="Altitud (m)"
                value={data.altitud_m ?? ''}
                name="altitud_m"
                type="number"
                onChange={onChange}
                />
                <Select
                label="Exposición a la altura"
                defaultNull
                onChange={e => setData(prevData => ({...prevData, exposicion_altura: e.target.value}))}
                value={data.exposicion_altura || ''}
                options={[{name: 'Gradual', value: 'gradual'}, {name: 'Rápida', value: 'rapida'}, {name: 'Inmediata', value: 'inmediata'}]}
                />
                <Select
                label="Demanda física"
                defaultNull
                onChange={e => setData(prevData => ({...prevData, demanda_fisica: e.target.value}))}
                value={data.demanda_fisica || ''}
                options={[{name: 'Baja', value: 'baja'}, {name: 'Media', value: 'media'}, {name: 'Alta', value: 'alta'}]}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Mín. días de aclimatación"
                value={data.min_dias_aclimatacion ?? ''}
                name="min_dias_aclimatacion"
                type="number"
                onChange={onChange}
                />
                <GeneralInput
                label="Advertencia obligatoria (⚠️ en cotización)"
                value={data.advertencia_obligatoria || ''}
                name="advertencia_obligatoria"
                onChange={onChange}
                />
            </div>
            <div className="input-container">
                <Checkbox
                checked={data.active}
                label="Active"
                containerStyle={{marginBottom: '10px'}}
                onChange={(checked: boolean) => setData(prevData => ({...prevData, active: checked}))}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Overview (en)"
                value={data.overview || ''}
                name="overview"
                onChange={onChange}
                />
                <GeneralInput
                label="Overview (es)"
                value={data.overview_es || ''}
                name="overview_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Overview (pt)"
                value={data.overview_pt || ''}
                name="overview_pt"
                onChange={onChange}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Description (en)"
                value={data.description || ''}
                name="description"
                onChange={onChange}
                />
                <GeneralInput
                label="Description (es)"
                value={data.description_es || ''}
                name="description_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Description (pt)"
                value={data.description_pt || ''}
                name="description_pt"
                onChange={onChange}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Cancellation policy (en)"
                value={data.cancellation_policy || ''}
                name="cancellation_policy"
                onChange={onChange}
                />
                <GeneralInput
                label="Cancellation policy (es)"
                value={data.cancellation_policy_es || ''}
                name="cancellation_policy_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Cancellation policy (pt)"
                value={data.cancellation_policy_pt || ''}
                name="cancellation_policy_pt"
                onChange={onChange}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Recommendations (en)"
                value={data.recommendations || ''}
                name="recommendations"
                onChange={onChange}
                />
                <GeneralInput
                label="Recommendations (es)"
                value={data.recommendations_es || ''}
                name="recommendations_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Recommendations (pt)"
                value={data.recommendations_pt || ''}
                name="recommendations_pt"
                onChange={onChange}
                />
            </div>
            <h2>Includes</h2>
            <Select
            value={''}
            defaultNull
            options={includes_options}
            onChange={onChangeIncludes}
            />
            <div className="list" 
            style={{margin: '10px 0'}}
            >
                {
                    data.include?.map((i, idx) => {
                        return (
                            <Component 
                            key={idx}
                            close={() => deleteIncludes(i)}
                            >
                                {i}
                            </Component>
                        )
                    })
                }
            </div>
            <h2>Detailed description</h2>
            <div className="actions">
                <button
                style={{float: 'initial'}}
                type="button"
                onClick={addDescription}
                >
                    +
                </button>
            </div>
            <div className="list">
                {
                    data.detailed_description?.map((dd, idx) => {
                        return (
                            <InputGroup
                            key={idx}
                            >
                                <h3>{idx + 1}.</h3>
                                <div className="delete" onClick={() => deleteDescription(idx)}>
                                    <AiOutlineCloseCircle/>
                                </div>
                                <div className="input-container">
                                    <GeneralInput
                                    label="Nombre (en)"
                                    value={dd.name || ''}
                                    name="name"
                                    onChange={e => onChangeDesc(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Nombre (es)"
                                    value={dd.name_es || ''}
                                    name="name_es"
                                    onChange={e => onChangeDesc(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Nombre (pt)"
                                    value={dd.name_pt || ''}
                                    name="name_pt"
                                    onChange={e => onChangeDesc(e, idx)}
                                    />
                                </div>
                                <div className="input-container">
                                    <GeneralInput
                                    label="Description (en)"
                                    value={dd.description || ''}
                                    name="description"
                                    onChange={e => onChangeDesc(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Description (es)"
                                    value={dd.description_es || ''}
                                    name="description_es"
                                    onChange={e => onChangeDesc(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Description (pt)"
                                    value={dd.description_pt || ''}
                                    name="description_pt"
                                    onChange={e => onChangeDesc(e, idx)}
                                    />
                                </div>
                            </InputGroup>
                        )
                    })
                }
            </div>
            <h2>FAQ</h2>
            <div className="actions">
                <button
                style={{float: 'initial'}}
                type="button"
                onClick={addFAQ}
                >
                    +
                </button>
            </div>
            <div className="list">
                {
                    data.faq?.map((dd, idx) => {
                        return (
                            <InputGroup
                            key={idx}
                            >
                                <h3>{idx + 1}.</h3>
                                <div className="input-container">
                                    <div className="delete" onClick={() => deleteFAQ(idx)}>
                                        <AiOutlineCloseCircle/>
                                    </div>
                                    <GeneralInput
                                    label="Question (en)"
                                    value={dd.question || ''}
                                    name="question"
                                    onChange={e => onChangeFAQ(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Question (es)"
                                    value={dd.question_es || ''}
                                    name="question_es"
                                    onChange={e => onChangeFAQ(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Question (pt)"
                                    value={dd.question_pt || ''}
                                    name="question_pt"
                                    onChange={e => onChangeFAQ(e, idx)}
                                    />
                                </div>
                                <div className="input-container">
                                    <GeneralInput
                                    label="Answer (en)"
                                    value={dd.answer || ''}
                                    name="answer"
                                    onChange={e => onChangeFAQ(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Answer (es)"
                                    value={dd.answer_es || ''}
                                    name="answer_es"
                                    onChange={e => onChangeFAQ(e, idx)}
                                    />
                                    <GeneralInput
                                    label="Answer (pt)"
                                    value={dd.answer_pt || ''}
                                    name="answer_pt"
                                    onChange={e => onChangeFAQ(e, idx)}
                                    />
                                </div>
                            </InputGroup>
                        )
                    })
                }
            </div>
            <h2>Entradas a parques</h2>
            <p style={{margin: '0 0 8px', fontSize: 13, color: '#777'}}>Tarifas que Molantur gestiona y cobra. Si el tour no tiene entrada, deja esta sección vacía.</p>
            <div className="actions">
                <button style={{float: 'initial'}} type="button" onClick={addEntrada}>+</button>
            </div>
            <div className="list">
                {
                    data.entradas?.map((en, idx) => (
                        <div key={idx} style={{borderBottom: '1px solid #eee', paddingBottom: 10, marginBottom: 10}}>
                            <h3>{idx + 1}.</h3>
                            <div className="input-container">
                                <GeneralInput
                                label="Parque (ej. Laguna Cejar, Tebinquinche)"
                                value={en.parque || ''}
                                onChange={e => updateEntrada(idx, 'parque', e.target.value)}
                                />
                                <Select
                                label="Categoría"
                                defaultNull
                                value={en.categoria || ''}
                                onChange={e => updateEntrada(idx, 'categoria', e.target.value)}
                                options={CATEGORIAS.map(c => ({name: c.charAt(0).toUpperCase() + c.slice(1), value: c}))}
                                />
                                <Select
                                label="Nacionalidad"
                                value={en.nacionalidad || 'ambos'}
                                onChange={e => updateEntrada(idx, 'nacionalidad', e.target.value)}
                                options={[{name: 'Ambos', value: 'ambos'}, {name: 'Chileno', value: 'chileno'}, {name: 'Extranjero', value: 'extranjero'}]}
                                />
                                <GeneralInput
                                label="Precio CLP"
                                type="number"
                                value={en.precio_clp ?? ''}
                                onChange={e => updateEntrada(idx, 'precio_clp', e.target.value)}
                                />
                            </div>
                            <div className="input-container">
                                <GeneralInput
                                label="Edad mín. (opcional)"
                                type="number"
                                value={en.edad_min ?? ''}
                                onChange={e => updateEntrada(idx, 'edad_min', e.target.value)}
                                />
                                <GeneralInput
                                label="Edad máx. (opcional)"
                                type="number"
                                value={en.edad_max ?? ''}
                                onChange={e => updateEntrada(idx, 'edad_max', e.target.value)}
                                />
                                <Checkbox
                                checked={!!en.requiere_acreditacion}
                                label="Requiere acreditación"
                                onChange={(checked: boolean) => updateEntrada(idx, 'requiere_acreditacion', checked)}
                                />
                            </div>
                            <div className="actions">
                                <button style={{float: 'initial', background: '#e25555'}} type="button" onClick={() => deleteEntrada(idx)}>Eliminar entrada</button>
                            </div>
                        </div>
                    ))
                }
            </div>
            <div className="input-container">
                <FileInput
                value={data.photo1}
                style={{height: '100px'}}
                label="Photo 1 (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => handleFileChange(newFile, 'photo1')}
                deleteFile={() => setData(prevData => {
                    const { photo1, ...rest } = prevData;
                    return rest;
                })}
                />
                <FileInput
                value={data.photo2}
                style={{height: '100px'}}
                label="Photo 2 (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => handleFileChange(newFile, 'photo2')}
                deleteFile={() => setData(prevData => {
                    const { photo2, ...rest } = prevData;
                    return rest;
                })}
                />
                <FileInput
                value={data.photo3}
                style={{height: '100px'}}
                label="Photo 3 (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => handleFileChange(newFile, 'photo3')}
                deleteFile={() => setData(prevData => {
                    const { photo3, ...rest } = prevData;
                    return rest;
                })}
                />
            </div>
            <div className="input-container">
                <FileInput
                value={data.photo4}
                style={{height: '100px'}}
                label="Photo 4 (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => handleFileChange(newFile, 'photo4')}
                deleteFile={() => setData(prevData => {
                    const { photo4, ...rest } = prevData;
                    return rest;
                })}
                />
                <FileInput
                value={data.photo5}
                style={{height: '100px'}}
                label="Photo 5 (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => handleFileChange(newFile, 'photo5')}
                deleteFile={() => setData(prevData => {
                    const { photo5, ...rest } = prevData;
                    return rest;
                })}
                />
                <FileInput
                value={data.photo6}
                style={{height: '100px'}}
                label="Photo 6 (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => handleFileChange(newFile, 'photo6')}
                deleteFile={() => setData(prevData => {
                    const { photo6, ...rest } = prevData;
                    return rest;
                })}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                value={data.pickup_range || ''}
                label="Pickup Range"
                name="pickup_range"
                onChange={onChange}
                placeholder="e.g. 7:00 - 8:00 AM"
                />
                <GeneralInput
                value={data.returning_time || ''}
                label="Returning Time"
                name="returning_time"
                onChange={onChange}
                placeholder="e.g. 8:00 PM"
                />
            </div>
            <div className="input-container">
                <FileInput
                value={data.cover}
                style={{height: '100px'}}
                label="Cover (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => handleFileChange(newFile, 'cover')}
                deleteFile={() => setData(prevData => {
                    const { cover, ...rest } = prevData;
                    return rest;
                })}
                />
            </div>
            <div className="actions"
            style={{height: '2em'}}
            >
                <button
                >
                    Editar
                </button>
            </div>
        </Modal>
    )
}

const InputGroup = styled.div`
margin-bottom: 20px;
position: relative;
.delete {
    position: absolute;
    right: 0;
    top: 0;
    font-size: 1.5rem;
    cursor: pointer;
}
`